/** Piezas que comparten los tres documentos del cajón.
 *
 *  Viven aquí y no en el cajón porque el packing list, el HS code y el
 *  multimodal se pintan con las MISMAS piezas: la misma celda editable, el mismo
 *  guion ámbar para un hueco, la misma banda de avisos. Duplicarlas dejaría que
 *  tres documentos que salen de la misma empresa el mismo día se vieran
 *  distintos, que es peor que el trabajo de mantenerlas juntas.
 */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  DocumentoAviso,
  DocumentoOrigen,
  PackingListCabeceraEditable,
} from '@/types'

/** Peso en kg con dos decimales. `null` no es 0: se pinta como hueco. */
export function fmtKg(n?: number | null) {
  if (n == null) return null
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}


export function fmtEnt(n?: number | null) {
  if (n == null) return null
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}


export function Seccion({
  titulo, nota, dark, accion, children,
}: {
  titulo: string
  nota?: string
  dark: boolean
  accion?: React.ReactNode
  children: React.ReactNode
}) {
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const dimTxt = dark ? 'text-slate-600' : 'text-slate-400'
  return (
    <section>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className={`text-[10px] font-bold uppercase tracking-wider ${mutedTxt}`}>{titulo}</h3>
          {nota && <p className={`text-[10px] mt-0.5 ${dimTxt}`}>{nota}</p>}
        </div>
        {accion}
      </div>
      <div className="mt-2">{children}</div>
    </section>
  )
}


/** Elige de dónde salen palets y pesos.
 *
 *  Va aquí, pegado a la tabla que cambia, y no en el raíl: el control tiene que
 *  estar donde se ve su efecto. El criterio automático se marca para que quede
 *  claro qué se está desviando. */
export function OrigenSelector({
  dark, activo, auto, onCambio,
}: {
  dark: boolean
  activo: DocumentoOrigen
  auto: DocumentoOrigen
  onCambio: (o: DocumentoOrigen | null) => void
}) {
  const { t } = useTranslation()
  const opciones: DocumentoOrigen[] = ['real', 'estimado']

  return (
    <div className="flex flex-col items-end gap-1">
      <div className={`flex rounded-md overflow-hidden border ${
        dark ? 'border-white/10' : 'border-slate-200'}`}>
        {opciones.map(o => {
          const sel = activo === o
          return (
            <button
              key={o}
              type="button"
              onClick={() => onCambio(o === auto ? null : o)}
              title={t(`salesOrders.documentos.origen.${o}Tooltip`)}
              aria-pressed={sel}
              className={`px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                sel
                  ? (dark ? 'bg-sky-500/20 text-sky-200' : 'bg-sky-100 text-sky-800')
                  : `cursor-pointer ${dark
                      ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`
              }`}
            >
              {t(`salesOrders.documentos.origen.${o}`)}
              {o === auto && (
                <span className={`ml-1 font-normal ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
                  {t('salesOrders.documentos.origen.auto')}
                </span>
              )}
            </button>
          )
        })}
      </div>
      {activo !== auto && (
        <p className={`text-[9px] ${dark ? 'text-amber-400/80' : 'text-amber-600'}`}>
          {t('salesOrders.documentos.origen.forzado')}
        </p>
      )}
    </div>
  )
}


export function Chip({
  texto, tono, dark,
}: { texto: string; tono: 'neutro' | 'indigo' | 'contorno' | 'verde' | 'ambar' | 'violeta'; dark: boolean }) {
  const map = {
    neutro:   dark ? 'bg-white/8 text-slate-400'          : 'bg-slate-100 text-slate-500',
    indigo:   dark ? 'bg-indigo-500/15 text-indigo-300'   : 'bg-indigo-100 text-indigo-700',
    contorno: dark ? 'border border-white/15 text-slate-400' : 'border border-slate-300 text-slate-500',
    violeta:  dark ? 'bg-violet-500/15 text-violet-300'   : 'bg-violet-100 text-violet-700',
    verde:    dark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-100 text-emerald-700',
    ambar:    dark ? 'bg-amber-500/15 text-amber-300'     : 'bg-amber-100 text-amber-700',
  }
  return (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${map[tono]}`}>
      {texto}
    </span>
  )
}


/** De dónde salen las cifras. Verde para el dato de almacén, ámbar para la
 *  estimación: un packing estimado es legítimo, pero hay que saber que lo es. */
export function OrigenBadge({ origen, dark }: { origen: 'real' | 'estimado'; dark: boolean }) {
  const { t } = useTranslation()
  return (
    <span title={t(`salesOrders.documentos.origen.${origen}Tooltip`)}>
      <Chip
        texto={t(`salesOrders.documentos.origen.${origen}`)}
        tono={origen === 'real' ? 'verde' : 'ambar'}
        dark={dark}
      />
    </span>
  )
}


/** Una pestaña del raíl.
 *
 *  Los tres documentos están siempre disponibles. Que el multimodal no proceda
 *  para una carga concreta —aérea, o sin mercancía peligrosa— lo explica su
 *  panel al abrirlo, y no un botón en gris: el motivo es del ENVÍO, no de la
 *  función, y merece una frase entera. */
export function TipoBoton({
  clave, activo, dark, onClick,
}: { clave: string; activo: boolean; dark: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  const base = 'flex flex-col items-start gap-0.5 px-4 py-2 text-left w-full transition-colors'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={activo ? 'page' : undefined}
      className={`${base} ${activo
        ? `${dark ? 'bg-white/[0.06]' : 'bg-white'} border-l-2 border-sky-500`
        : `cursor-pointer border-l-2 border-transparent ${dark
            ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-100'}`}`}
    >
      <span className={`text-[12px] ${activo
        ? `font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}`
        : (dark ? 'text-slate-400' : 'text-slate-500')}`}>
        {t(`salesOrders.documentos.tipos.${clave}`)}
      </span>
    </button>
  )
}


/** Celda que distingue «no hay dato» de «cero».
 *
 *  Un hueco va con guion y en ámbar: es lo que saldrá en blanco en el PDF, y
 *  verlo aquí es toda la razón de ser de esta tabla. */
export function Celda({
  td, dark, valor, alinear,
}: { td: string; dark: boolean; valor: string | null; alinear?: 'right' }) {
  const align = alinear === 'right' ? 'text-right tabular-nums' : ''
  if (valor == null || valor === '') {
    return (
      <td className={`${td} ${align} ${dark ? 'text-amber-400/70' : 'text-amber-600/80'}`}>—</td>
    )
  }
  return <td className={`${td} ${align}`}>{valor}</td>
}


/** Celda corregible.
 *
 *  Se ve como texto hasta que se entra en ella: la tabla es sobre todo para
 *  REVISAR, y una rejilla de cajas de texto la vuelve ilegible de un vistazo.
 *
 *  Tres estados que hay que distinguir a simple vista:
 *   · hueco (—, ámbar): el sistema no tiene el dato y saldrá en blanco en el PDF.
 *   · corregido (anillo ámbar): lo ha puesto una persona; el tooltip dice el
 *     valor del sistema, porque un valor tecleado y uno leído son idénticos a la
 *     vista y hay que poder distinguirlos.
 *   · calculado (candado): se recalcula solo; el candado lo congela. */
/** Campos que se escriben con letras. Todo lo demás se alinea a la derecha y
 *  pide teclado numérico. Un código HS es texto aunque parezca un número: tiene
 *  ceros a la izquierda que un `number` se comería. */
const CAMPOS_DE_TEXTO = new Set(['descripcion', 'hscode', 'origen', 'lote'])

export function CeldaEditable({
  dark, td, campo, valor, original, texto, corregido, fijado, derivado, bloqueado, plano,
  onCambio, onFijar,
}: {
  dark: boolean
  td: string
  /** El nombre del campo, no su tipo: de él sale si la celda se alinea a la
   *  derecha y si el teclado del móvil sale numérico. Es `string` y no la unión
   *  de un documento porque la misma celda sirve al packing y al HS code, que
   *  tienen campos distintos. */
  campo: string
  valor: string | number | null | undefined
  original: string | number | null | undefined
  texto: string | null
  corregido: boolean
  fijado: boolean
  derivado: boolean
  bloqueado: boolean
  plano?: boolean
  onCambio: (v: string) => void
  onFijar: () => void
}) {
  const { t } = useTranslation()
  const [editando, setEditando] = useState(false)
  const esTexto = CAMPOS_DE_TEXTO.has(campo)
  const hueco = valor == null || valor === ''

  const alinear = esTexto ? '' : 'text-right tabular-nums'
  const colorTexto = hueco
    ? (dark ? 'text-amber-400/70' : 'text-amber-600/80')
    : ''
  const anillo = corregido
    ? (dark ? 'ring-1 ring-amber-400/50 bg-amber-400/5' : 'ring-1 ring-amber-400 bg-amber-50')
    : ''

  const titulo = corregido
    ? t('salesOrders.documentos.correcciones.originalTooltip', {
        valor: original == null || original === '' ? '—' : String(original),
      })
    : undefined

  const entrada = (
    <input
      type={esTexto ? 'text' : 'text'}
      inputMode={esTexto ? 'text' : 'decimal'}
      autoFocus={editando}
      defaultValue={valor == null ? '' : String(valor)}
      disabled={bloqueado}
      onBlur={e => { setEditando(false); onCambio(e.target.value) }}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') setEditando(false)
      }}
      className={`w-full min-w-0 px-1 py-0.5 rounded text-[11px] outline-none ${alinear} ${
        dark ? 'bg-white/10 text-slate-100 ring-1 ring-sky-500/60'
             : 'bg-white text-slate-900 ring-1 ring-sky-400'
      }`}
    />
  )

  const contenido = editando ? entrada : (
    <button
      type="button"
      disabled={bloqueado}
      onClick={() => setEditando(true)}
      title={titulo}
      className={`w-full px-1 py-0.5 rounded ${alinear} ${colorTexto} ${anillo} ${
        bloqueado ? 'cursor-not-allowed' : 'cursor-text'
      } ${!bloqueado && !corregido
            ? (dark ? 'hover:bg-white/8' : 'hover:bg-slate-100')
            : ''}`}
    >
      {texto ?? '—'}
    </button>
  )

  if (plano) return contenido

  return (
    <td className={td}>
      <div className="flex items-center gap-0.5 justify-end">
        {contenido}
        {derivado && (
          <button
            type="button"
            disabled={bloqueado}
            onClick={onFijar}
            aria-pressed={fijado}
            title={t(fijado
              ? 'salesOrders.documentos.correcciones.soltarTooltip'
              : 'salesOrders.documentos.correcciones.fijarTooltip')}
            className={`shrink-0 p-0.5 rounded transition-colors ${
              bloqueado ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
            } ${fijado
                  ? (dark ? 'text-sky-300' : 'text-sky-600')
                  : (dark ? 'text-slate-700 hover:text-slate-400' : 'text-slate-300 hover:text-slate-500')}`}
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="11" width="14" height="10" rx="2" />
              {fijado
                ? <path d="M8 11V7a4 4 0 018 0v4" />
                : <path d="M8 11V7a4 4 0 017.5-2" />}
            </svg>
          </button>
        )}
      </div>
    </td>
  )
}


export function Banda({
  tono, dark, children,
}: { tono: 'rojo' | 'ambar'; dark: boolean; children: React.ReactNode }) {
  const cls = tono === 'rojo'
    ? (dark ? 'bg-red-500/10 border-red-500/25 text-red-300' : 'bg-red-50 border-red-200 text-red-700')
    : (dark ? 'bg-amber-500/10 border-amber-500/25 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800')
  return <div className={`rounded-lg border px-3 py-2 text-[11px] leading-relaxed ${cls}`}>{children}</div>
}


/** El `detail` de la HTTPException del backend, que ya viene redactado para el
 *  usuario (qué falta, qué artículo). Si no hay, el llamante pone el genérico. */
export function mensajeDeError(e: unknown): string | null {
  const detalle = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detalle === 'string' && detalle.trim() ? detalle : null
}


/** Un campo del formulario de envío. */
export interface CampoCabecera {
  clave: keyof PackingListCabeceraEditable
  tipo?: 'date'
}

/** Los datos de envío que D365 no tiene y hay que teclear.
 *
 *  La lista de campos la pasa cada documento: el packing pide factura, incoterm
 *  y consignatario; el multimodal pide además puertos, transitario y declarante.
 *  Enseñárselos todos a quien viene a sacar un packing sería pedirle ocho datos
 *  que su documento ni imprime.
 *
 *  El estado es UNO solo para los tres, y eso es deliberado: son el mismo envío,
 *  así que la fecha o el buque tecleados para el packing valen igual para el
 *  multimodal. */
export function Formulario({
  dark, form, campos, onCambio, titulo, nota,
}: {
  dark: boolean
  form: PackingListCabeceraEditable
  campos: readonly CampoCabecera[]
  onCambio: (clave: keyof PackingListCabeceraEditable, valor: string) => void
  titulo?: string
  nota?: string
}) {
  const { t } = useTranslation()
  const label = `text-[9px] font-bold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const input = `w-full mt-1 px-2 py-1.5 rounded-md text-[11px] border outline-none transition-colors ${
    dark
      ? 'bg-white/[0.04] border-white/10 text-slate-200 placeholder:text-slate-600 focus:border-sky-500/60'
      : 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-300 focus:border-sky-400'
  }`

  return (
    <Seccion
      titulo={titulo ?? t('salesOrders.documentos.form.title')}
      nota={nota ?? t('salesOrders.documentos.form.noPersiste')}
      dark={dark}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {campos.map(({ clave, tipo }) => (
          <div key={clave}>
            <label className={label} htmlFor={`pl-${clave}`}>
              {t(`salesOrders.documentos.form.${clave}`)}
            </label>
            <input
              id={`pl-${clave}`}
              type={tipo ?? 'text'}
              value={(form[clave] as string) ?? ''}
              onChange={e => onCambio(clave, e.target.value)}
              placeholder={t('salesOrders.documentos.form.vacio')}
              className={input}
            />
          </div>
        ))}
      </div>
    </Seccion>
  )
}

/** El pie del cajón: qué falta, qué se ha corregido y el botón de generar.
 *
 *  Lo comparten los tres documentos porque las tres cosas que dice valen para
 *  los tres, y la última —cuántos valores ha tocado una persona— es la que no
 *  puede faltar en ninguno: un documento corregido a mano no puede parecer uno
 *  que salió tal cual del sistema. */
export function PieGenerar({
  dark, errorGen, bloqueantes, listas, numCorregidos, numExcluidas,
  onRestablecer, puedeGenerar, generar, onGenerar,
}: {
  dark: boolean
  errorGen: string | null
  bloqueantes: number
  /** Qué hay listo, en la unidad de cada documento: líneas, lotes, mercancías. */
  listas: string
  numCorregidos: number
  numExcluidas: number
  onRestablecer: () => void
  puedeGenerar: boolean
  /** Rótulo del botón. Por omisión, el del packing. */
  generar?: string
  onGenerar: () => void
}) {
  const { t } = useTranslation()
  const border = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'

  return (
    <div className={`flex items-center gap-3 px-6 py-3.5 border-t ${border} shrink-0`}>
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] ${mutedTxt}`}>
          {errorGen
            ? <span className={dark ? 'text-red-400' : 'text-red-600'}>{errorGen}</span>
            : bloqueantes > 0
              ? <span className={dark ? 'text-red-400' : 'text-red-600'}>
                  {t('salesOrders.documentos.bloqueadoPor', { count: bloqueantes })}
                </span>
              : listas}
        </p>
        {(numCorregidos > 0 || numExcluidas > 0) && (
          <p className={`text-[10px] mt-0.5 ${dark ? 'text-amber-400/90' : 'text-amber-700'}`}>
            {numCorregidos > 0 && t('salesOrders.documentos.correcciones.resumen', { count: numCorregidos })}
            {numCorregidos > 0 && numExcluidas > 0 && ' · '}
            {numExcluidas > 0 && t('salesOrders.documentos.correcciones.excluidas', { count: numExcluidas })}
            {' · '}
            <button
              type="button"
              onClick={onRestablecer}
              className="underline underline-offset-2 cursor-pointer hover:no-underline"
            >
              {t('salesOrders.documentos.correcciones.restablecer')}
            </button>
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onGenerar}
        disabled={!puedeGenerar}
        title={puedeGenerar ? undefined : t('salesOrders.documentos.bloqueadoTooltip')}
        className={`px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors ${
          puedeGenerar
            ? 'bg-sky-600 hover:bg-sky-500 text-white cursor-pointer'
            : `cursor-not-allowed ${dark ? 'bg-white/5 text-slate-600' : 'bg-slate-100 text-slate-400'}`
        }`}
      >
        {generar ?? t('salesOrders.documentos.generar')}
      </button>
    </div>
  )
}

/** La banda de avisos, con los bloqueantes en rojo y el resto en ámbar.
 *
 *  Traduce por código y no por texto: el servidor no sabe en qué idioma está la
 *  pantalla, y un mensaje redactado allí saldría en español para todo el mundo. */
export function BloqueAvisos({
  dark, bloqueantes, informativos,
}: { dark: boolean; bloqueantes: DocumentoAviso[]; informativos: DocumentoAviso[] }) {
  const { t } = useTranslation()
  if (bloqueantes.length === 0 && informativos.length === 0) return null

  const texto = (a: DocumentoAviso) => {
    const base = t(`salesOrders.documentos.avisos.${a.codigo}`, {
      articulo: a.articulo ?? '',
      ...(a.valores ?? {}),
      defaultValue: a.codigo,
    })
    // El backend agrupa por referencia: una carga aérea con 200 bultos de la
    // misma repetía el mismo aviso 200 veces. El recuento va como sufijo para no
    // duplicar cada mensaje, y sólo en los avisos que son de un artículo.
    const veces = a.articulo ? Number(a.valores?.lineas ?? 0) : 0
    return veces > 1
      ? base + t('salesOrders.documentos.avisos.enLineas', { count: veces })
      : base
  }

  return (
    <Seccion titulo={t('salesOrders.documentos.avisos.title')} dark={dark}>
      <div className="space-y-2">
        {bloqueantes.length > 0 && (
          <Banda tono="rojo" dark={dark}>
            <ul className="space-y-0.5">
              {bloqueantes.map((a, i) => <li key={`b-${i}`}>{texto(a)}</li>)}
            </ul>
          </Banda>
        )}
        {informativos.length > 0 && (
          <Banda tono="ambar" dark={dark}>
            <ul className="space-y-0.5">
              {informativos.map((a, i) => <li key={`i-${i}`}>{texto(a)}</li>)}
            </ul>
          </Banda>
        )}
      </div>
    </Seccion>
  )
}
